import { Backdrop, Box, CardContent, Grid, Table, TableCell, alpha, styled } from '@mui/material';
import { Button, Pagination, Typography } from '@totalsoft/rocket-ui';

export const Qr = styled('img')(({ theme }) => ({
  cursor: 'pointer',
  position: 'relative',
  zIndex: '99',
  width: theme.spacing(6),
  borderRadius: theme.spacing(1.5)
}));

export const BulkActionsContainer = styled(Grid)(({ theme }) => ({
  background: alpha(theme.palette.primary.main, 0.15),
  borderRadius: theme.spacing(1)
}));

export const WarningTypography = styled(Typography)(({ theme }) => ({
  color: theme.palette.warning.main
}));

export const BulkActionsTriangle = styled('div')(({ theme }) => ({
  width: 0,
  height: 0,
  borderStyle: 'solid',
  borderWidth: `${theme.spacing(2.25)} ${theme.spacing(1.25)} 0 ${theme.spacing(1.25)}`,
  borderColor: `${alpha(theme.palette.primary.main, 0.15)} transparent transparent transparent`,
  marginLeft: theme.spacing(4),
  marginBottom: theme.spacing(2)
}));

export const TableStyled = styled(Table)(({ theme }) => ({
  [theme.breakpoints.down('md')]: {
    minWidth: theme.spacing(100),
    '& td': {
      padding: theme.spacing(1),
      wordBreak: 'break-all'
    }
  }
}));

export const DenseHeaderTableCell = styled(TableCell)(({ theme }) => ({
  py: theme.spacing(0.5),
  px: theme.spacing(1.25),
  fontWeight: 'bold',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  maxWidth: 0,
  overflow: 'hidden'
}));

export const DenseBodyTableCell = styled(TableCell)(({ theme }) => ({
  py: theme.spacing(0.5),
  px: theme.spacing(1.25),
  maxWidth: 0,
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(0.5),
    '&:last-child ': {
      padding: `${theme.spacing(0.5)} !important`
    }
  }
}));

export const BackdropCustom = styled(Backdrop, {
  name: 'MuiModal',
  slot: 'Backdrop',
  overridesResolver: (props, styles) => {
    return styles.backdrop;
  }
})({
  zIndex: -1,
  background: 'rgba(0, 0, 0, 0.15)',
  backdropFilter: 'blur(7.5px)'
});

export const AddSigneeButton = styled(Button, { shouldForwardProp: prop => !['isLargeButton'].includes(prop) })(
  ({ theme, isLargeButton }) => ({
    width: '100%',
    minHeight: theme.spacing(12.5),
    borderRadius: theme.spacing(1),
    fontWeight: 'bold',
    border: `${theme.spacing(0.25)} solid ${theme.palette.primary.main}`,
    background: alpha(theme.palette.primary.main, 0.1),
    height: isLargeButton ? theme.spacing(25) : 'auto'
  })
);

export const SigneeCard = styled(Grid)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  padding: `${theme.spacing(3)} ${theme.spacing(4)}`,
  marginTop: theme.spacing(3),
  boxShadow: `0px 0px ${theme.spacing(1.25)} ${theme.spacing(0.1)} rgba(0, 0, 0, 0.2)`,
  borderRadius: theme.spacing(1),
  overflow: 'visible',
  background: 'none !important'
}));

export const ArrowButtonsContainer = styled(Grid)(() => ({
  '& > div': {
    display: 'flex',
    justifyContent: 'center'
  }
}));

export const DialogTitle = styled(Typography)(({ theme }) => ({
  fontSize: theme.spacing(3),
  fontWeight: 'bold'
}));

export const DialogButton = styled(Button)(({ theme }) => ({
  minWidth: theme.spacing(12.5)
}));

export const SecondDialogButton = styled(DialogButton)(({ theme }) => ({
  marginLeft: theme.spacing(1)
}));

export const EntityContainer = styled(Grid)(({ theme }) => ({
  '& > *': {
    paddingBottom: theme.spacing(2)
  },
  '&:last-child': {
    paddingBottom: theme.spacing(0)
  }
}));

export const EntityTitle = styled(Grid)(({ theme }) => ({
  position: 'sticky',
  left: '0px',
  top: theme.spacing(8.5),
  fontWeight: '600',
  fontSize: theme.spacing(2.5),
  lineHeight: '140%',
  color: 'black',
  backgroundColor: 'white',
  zIndex: '1',
  paddingTop: theme.spacing(2),
  boxShadow: `${theme.spacing(1.5)} 0 0 0 white, -${theme.spacing(1.5)} 0 0 0 white`
}));

export const ChecklistCardContent = styled(CardContent)(({ theme }) => ({
  padding: `${theme.spacing(2)} ${theme.spacing(1.25)} !important`,
  margin: 0
}));

export const StateColorBox = styled(Box)(({ theme }) => ({
  width: theme.spacing(1),
  height: '100%'
}));

export const UploadedFileText = styled('span', { shouldForwardProp: prop => !['disabledDropzone'].includes(prop) })(
  ({ theme, disabledDropzone }) => ({
    color: !disabledDropzone && '#2EB243',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 'bold',
    marginBottom: theme.spacing(0.75)
  })
);

export const UploadDocumentGrid = styled(Grid)(({ theme }) => ({
  height: theme.spacing(14.25),
  [theme.breakpoints.down('lg')]: {
    width: '100%',
    marginBottom: theme.spacing(3)
  },
  [theme.breakpoints.up('lg')]: {
    width: theme.spacing(20.5),
    margin: `auto ${theme.spacing(3)} auto 0`
  }
}));

export const DropzoneAreaBaseWrapper = styled('div', {
  shouldForwardProp: prop => !['disabledDropzone', 'isUploaded'].includes(prop)
})(({ theme, disabledDropzone, isUploaded }) => ({
  height: '100%',
  width: '100%',
  '& > div': {
    minHeight: '0px !important',
    height: '100%',
    width: '100%',
    fontSize: theme.spacing(1.75),
    fontWeight: 'bold',
    lineHeight: theme.spacing(2.5),
    color: disabledDropzone ? '#A3A3A3' : '#343434',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing(0.5),
    pointerEvents: disabledDropzone && 'none',
    clickable: false,
    borderRadius: theme.spacing(1),
    border: disabledDropzone
      ? `${theme.spacing(0.25)} solid #E2E2E2`
      : isUploaded
      ? `${theme.spacing(0.25)} dashed #2EB243`
      : `${theme.spacing(0.25)} dashed #004A7B`,
    background: disabledDropzone ? '#F1F1F1' : isUploaded ? '#F5FFF9' : '#EDF8FF',
    '& > div > p': {
      margin: 0
    }
  }
}));

export const CardTitle = styled(Typography)(({ theme }) => ({
  fontWeight: '600',
  fontSize: theme.spacing(2.4),
  lineHeight: '140%',
  paddingRight: theme.spacing(1)
}));

export const PaginationStyled = styled(Pagination)(() => ({
  '& > div > div > p ': {
    display: 'flex',
    alignItems: 'center',
    marginBottom: '0 !important'
  }
}));

export const PageNumbersContainer = styled('div')(({ theme }) => ({
  position: 'absolute',
  top: '80%',
  justifyContent: 'center',
  padding: `${theme.spacing(0.5)} ${theme.spacing(2)}`,
  wordSpacing: theme.spacing(1.5),
  color: 'white',
  fontWeight: '600',
  borderRadius: theme.spacing(0.75),
  background: alpha(theme.palette.grey[500], 0.75)
}));

export const PdfCanvasStyled = styled('div')(({ rotationAngle }) => ({
  transform: `rotate(${rotationAngle}deg)`,
  transition: 'transform 0.2s ease'
}));

export const ImageCanvasStyled = styled('div')(({ rotationAngle, scale }) => ({
  transform: `rotate(${rotationAngle}deg) scale(${scale})`,
  transition: 'transform 0.2s ease'
}));

export const PDFPreviewContainer = styled(Grid)(() => ({
  justifyContent: 'center',
  overflow: 'auto'
}));

export const TableGrid = styled(Grid)(()=>({
  height :' 400px',
  }));
  
export const AddDocumentsTableCell = styled(TableCell)(() =>({
      borderBottom: '2px solid rgb(0 0 0 / 40%)'
  
  }));